import { type FC, useMemo } from 'react';

import { sanitizeHref } from '@shared/dream-components';
import type { JSONContent } from '@tiptap/core';

import { toPostMediaImage } from '~/routes/community/_utils/toPostMediaImage';

import { PostMediaCarousel } from './ui/PostMediaCarousel';
import { PostMediaFrame, type PostMediaImage } from './ui/PostMediaFrame';

import { TiptapRenderer } from './TiptapRenderer';

interface TrailingMediaProps {
  nodes: JSONContent[];
  postId: string;
}

interface MediaRun {
  start: number;
  nodes: JSONContent[];
}

const isImage = (node: JSONContent): boolean => node.type === 'image';

// Consecutive images form one run that shares a carousel, or a frame when only one; every other node keeps its own run.
const groupRuns = (nodes: JSONContent[]): MediaRun[] =>
  nodes.reduce<MediaRun[]>((runs, node, index) => {
    const lastRun = runs.at(-1);
    if (lastRun && isImage(node) && isImage(lastRun.nodes[0])) {
      lastRun.nodes.push(node);
    } else {
      runs.push({ start: index, nodes: [node] });
    }
    return runs;
  }, []);

const toMediaImages = ({ start, nodes }: MediaRun): PostMediaImage[] =>
  nodes.flatMap((node, offset) => {
    const src = isImage(node) ? sanitizeHref(node.attrs?.src) : null;
    return src ? [toPostMediaImage(src, node.attrs?.alt ?? '', `image-${start + offset}`)] : [];
  });

export const TrailingMedia: FC<TrailingMediaProps> = ({ nodes, postId }) => {
  const runs = useMemo(
    () =>
      groupRuns(nodes).map((run) => ({
        key: `run-${run.start}`,
        images: toMediaImages(run),
        doc: { type: 'doc', content: run.nodes },
      })),
    [nodes],
  );

  return (
    <div data-testid="trailing-media">
      {runs.map(({ key, images, doc }) => {
        if (images.length > 1) return <PostMediaCarousel key={key} images={images} />;
        if (images.length === 1) return <PostMediaFrame key={key} images={images} className="my-3" />;
        return <TiptapRenderer key={key} tiptapState={doc} postId={postId} />;
      })}
    </div>
  );
};
